import { sanitizeInput } from 'utils/sanitizeInput';
import React, { useState, ChangeEvent, useEffect, ReactNode, useRef } from 'react';
import { Button, Modal, Stack, Image, Form } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import {
	dataLimits,
	S3_URL,
	S3_SMALL_THUMB,
	ALLOWEDVIDEOFORMATS,
	USER_TYPES,
} from 'constants/index';
import useCommonStore, { setIsLoading } from 'store/common';
import { addPostInitialValues, addPostSchema } from 'constants/schemaValidations';
import useTranslate from 'hooks/useTranslate';
import { useFormik } from 'formik';
import {
	convertJpegToPng,
	createThumbnailByVideo,
	extractTagsAndMentions,
	getFileExtension,
	getFileName,
	getImageAspectRatio,
} from 'utils';
import {
	logPostCreated,
	useAddAPostMutation,
	useBucketUrlMutation,
	useCheckHashTagsMutation,
	useFeedDetailsQuery,
	useFeedUpdateMutation,
	useGetPostMutation,
	useGetStaticPostMutation,
	useUploadFileMutation,
} from 'services';
import toast from 'react-hot-toast';
import { AddPostInterface } from 'constants/interfaces';
import useAuthStore from 'store/auth';
import MentionsComponent from 'components/Common/MentionsComponent/input';
import { setFeeds, setFeedsMeta, setLastFeedPage } from 'store/feeds';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { isMobile } from 'react-device-detect';
import useLocalizationStore from 'store/localization';
import { setForYouCurrentCursor, setForYouFeeds, setForYouFeedsMeta, setForYouLastcursor } from 'store/feedsForYou';

import {
	setFollowingFeeds,
	setFollowingFeedsMeta,
	setFollowingLastcursor,
	setFollowingCurrentCursor,
} from 'store/feedsFollowing';

const CreateNewPost: React.FC<any> = () => {
	const { user: authUser, token }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);
	const { translate } = useTranslate();
	const { modalConfig = {}, hideCommonModal }: any = useCommonStore((state) => state);
	const { id = null } = modalConfig;

	const { mutateAsync: bucketUrlMutation } = useBucketUrlMutation();
	const { mutateAsync: uploadFileMutation } = useUploadFileMutation();
	const { mutateAsync: addPostMutation } = useAddAPostMutation();
	const { mutateAsync: getPostMutation } = token
		? useGetPostMutation()
		: useGetStaticPostMutation();
	const { mutateAsync: checkHashTagsMutation } = useCheckHashTagsMutation();
	const { mutateAsync: feedUpdateMutation } = useFeedUpdateMutation();
	const { data: feedData = {} } = useFeedDetailsQuery(Number(id));
	const { activeLanguage }: any = useLocalizationStore((state) => state);

	const [error, setError] = useState<string | null>(null);
	const [feedDetails, setFeedDetails] = useState<any>({});
	const [openEmojiPicker, setOpenEmojiPicker] = useState<boolean>(false);

	const emojiRef = useRef<any>(null);
	const emojiBtnRef = useRef<any>(null);
	const lastFeedDataRef = useRef<string>('');
	const { postId, content } = feedDetails;

	useEffect(() => {
		if (id && feedData) {
			const feedDataKey = JSON.stringify(feedData);
			if (feedDataKey !== lastFeedDataRef.current) {
				lastFeedDataRef.current = feedDataKey;
				setFeedDetails(feedData);
			}
		}
	}, [id, feedData]);
	// const searchString = `order=DESC&page=${1}&limit=${dataLimits?.feedsListing}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected`;
	const searchString = `order=DESC&cursor=${0}&limit=${dataLimits?.feedsListing}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected`;
	const searchStringAll = `order=DESC&cursor=${0}&limit=${dataLimits?.feedsListing}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected&allUsers=true`;
	

	const uploadMedia = async (values: any) => {
		try {
			const mediaItems = values || [];
			const uploadPromises = mediaItems.map(async (media: any, index: number) => {
				const requestOptions = {
					method: 'PUT',
					headers: {
						'Content-Type': media.mediaType,
					},
					body: mediaData[index],
				};

				return uploadFileMutation({
					url: media.url,
					requestOptions,
				});
			});

			const responses = await Promise.all(uploadPromises);
			// Normalize responses because some upload helpers may return raw fetch Response objects
			const normalized = responses.map((response: any) => {
				if (response && typeof response === 'object' && 'success' in response) return response;
				return {
					success: !!response?.success || !!response?.ok,
					status: response?.status,
					data: response?.data ?? null,
					raw: response,
				};
			});

			if (normalized.every((r) => r.success)) {
				console.log('All files uploaded successfully:', normalized);
			} else {
				console.error('Some files failed to upload:', normalized);
			}
		} catch (error) {
			console.error('An error occurred during file uploads:', error);
		}
	};

	const [mediaData, setMediaData] = useState<any>([]);
	const [thumbnails, setThumbnails] = useState<string[]>([]);

	const handleMediaChange = async (event: ChangeEvent<HTMLInputElement>) => {
		setError(null);
		setIsLoading(true);

		if (!event.target.files) return;

		const newFiles = Array.from(event.target.files);

		// Filter valid files (image/video)
		const validFiles = newFiles.filter(
			(file) => file && (file.type.startsWith('image/') || file.type.startsWith('video/')),
		);
		const invalidFiles = newFiles.filter(
			(file) => file && !file.type.startsWith('image/') && !file.type.startsWith('video/'),
		);

		if (invalidFiles.length > 0) {
			setError(translate('ToastMessage.onlyImageAndVideoFilesAreAllowed'));
			setIsLoading(false);
			return;
		}

		let invalidVideoFormat = false;
		const allowedVideoFormatsString = ALLOWEDVIDEOFORMATS.map(
			(format: string) => format.split('/')[1],
		).join(', ');

		validFiles.forEach((file) => {
			if (file.type.startsWith('video/')) {
				if (!ALLOWEDVIDEOFORMATS.includes(file.type)) {
					invalidVideoFormat = true;
				}
			}
		});

		if (invalidVideoFormat) {
			setError(
				translate('ToastMessage.invalidVideoFormat', { formats: allowedVideoFormatsString }),
			);
			setIsLoading(false);
			return;
		}

		const convertedFilesPromises = validFiles.map(async (file) => {
			if (file.type.startsWith('image/') && file.type !== 'image/png') {
				const pngBlob = await convertJpegToPng(file); // Convert to PNG
				const pngFile = new File([pngBlob], file.name.replace(/\.[^/.]+$/, '.png'), {
					type: 'image/png',
				});
				return pngFile;
			}
			return file; // Return other files (e.g., video or other image formats) as they are
		});

		const convertedFiles = await Promise.all(convertedFilesPromises);

		const totalFiles = mediaData.length + convertedFiles.length;
		const allowedFiles =
			totalFiles > 10 ? convertedFiles.slice(0, 10 - mediaData.length) : convertedFiles;

		// Set the media data with the converted files
		setMediaData((prevMedia: any) => [...prevMedia, ...allowedFiles]);
		setIsLoading(false);
	};

	const handleRemoveMedia = (index: number) => {
		setIsLoading(true);
		const updatedMedia = mediaData.filter((_: any, i: number) => i !== index);
		setMediaData(updatedMedia);
		formik.setFieldValue('mediaItems', updatedMedia);
		setIsLoading(false);
	};

	useEffect(() => {
		if (mediaData?.length > 0 && !postId) {
			setIsLoading(true);
			formik.setFieldValue('mediaItems', mediaData);
			const fetchThumbnails = async () => {
				const thumbnailsArray: string[] = [];

				for (const file of mediaData) {
					const mediaType = file.type.startsWith('image/') ? 'image' : 'video';

					const thumbnailPromise = new Promise<string>((resolve) => {
						if (mediaType === 'video') {
							const objectUrl = URL.createObjectURL(file);
							createThumbnailByVideo(objectUrl)
								.then((thumbnail) => resolve(thumbnail))
								.catch(() => resolve(''));
						} else {
							// For images, use URL.createObjectURL directly
							const objectUrl = URL.createObjectURL(file);
							resolve(objectUrl);
						}
					});
					const thumbnail = await thumbnailPromise;
					thumbnailsArray.push(thumbnail);
				}
				setThumbnails(thumbnailsArray);
				setIsLoading(false);
			};

			(async () => {
				await fetchThumbnails();
			})();
		} else {
			setThumbnails([]);
		}
	}, [mediaData]);

	const formik: any = useFormik({
		initialValues: addPostInitialValues,
		validationSchema: addPostSchema(translate),
		enableReinitialize: true,
		onSubmit: async (values: any, { setFieldError, setFieldValue }) => {
			if (!values?.content.trim() && values?.mediaItems?.length === 0) {
				setFieldValue('content', values?.content.trim());
				setFieldError('content', translate('ToastMessage.contentCannotBeEmpty'));
				return;
			}
			setIsLoading(true);
			const formData: any[] = [];
			if (mediaData?.length > 0 && !postId) {
				const response = await bucketUrlMutation({
					type: 'SOCIAL_POST',
					extension: mediaData.map((file: any) => getFileExtension(file?.name)),
					contentType: mediaData.map((file: any) => file.type),
					originalFileName: mediaData.map((file: any) => file.name),
				});

				if (response?.success) {
					const uploadData = response?.data?.map((resData: any, index: number) => {
						const { type } = mediaData[index];
						return { ...resData, mediaType: type.startsWith('image/') ? 'image' : 'video' };
					});
					await uploadMedia(uploadData);

					for (let index = 0; index < response?.data?.length; index++) {
						const data = response.data[index];
						const isImage = mediaData[index].type.startsWith('image/') ? true : false;
						const aspectRatio = isImage ? await getImageAspectRatio(mediaData[index]) : '';

						formData.push({
							file: data?.location,
							mediaType: isImage ? 'image' : 'video',
							aspectRatio: aspectRatio,
						});
					}
					feedsSave(values, formData, setFieldValue);
				}
			} else {
				feedsSave(values, formData, setFieldValue);
			}
		},
	});

	const feedsSave = async (values: any, formData: any = [], setFieldValue: any) => {
		const { taggedUsers, hashTags } = await extractTagsAndMentions(values.content.trim());
		const hashTagList = hashTags?.length > 0 ? hashTags?.map((res) => res.name) : [];
		let oldHashTag: any = [];
		let newHashTagsArr: any = [];
		if (hashTagList?.length > 0) {
			await checkHashTagsMutation({ hashTag: hashTagList }).then((res) => {
				if (res.success) {
					oldHashTag = res?.data?.map((hash: any) => hash.id); // Array of IDs only
					const oldHashTagNames = res?.data?.map((hash: any) => hash.hashTag); // Array of hashTag names for case-sensitive check

					const remainingHashTag = hashTags.filter(
						(item) =>
							!oldHashTag.includes(item.id) && // Filter by ID
							!oldHashTagNames.includes(item.name), // Case-sensitive filter by name
					);

					newHashTagsArr = remainingHashTag.map((hash) => hash.name); // Array of new hashtag names
				}
			});
		}

		const modifiedData = sanitizeInput({
			...values,
			content: values?.content?.trim(),
			hashTags: oldHashTag,
			newHashTags: newHashTagsArr,
			taggedUsers,
		});

		if (postId) {
			modifiedData.id = postId;
			delete modifiedData?.mediaItems;
		} else {
			modifiedData.mediaItems = formData || [];
		}

		setFieldValue('content', values?.content.trim());
		const actionMutation = postId ? feedUpdateMutation : addPostMutation;

		try {
			const res = await actionMutation(modifiedData);
			if (res?.success) {
				toast.success(res?.message || translate('ToastMessage.postSavedSuccessfully'));
				const postRes = await getPostMutation(searchString);
				if (postRes?.success) {
					const { docs = {}, meta = {} } = postRes?.data || {};
					const { results = [] } = docs;
					if (results?.length > 0) {
									setFollowingFeeds(results);
												setFollowingFeedsMeta(results.meta);
												setFollowingCurrentCursor(results.meta?.nextCursor || 0);
												setFollowingLastcursor(results.meta?.nextCursor || 0);

						// setFeeds(results);
						// setFeedsMeta(meta);
						// setLastFeedPage(meta?.page || 1);
					}
				}


				const postResAll = await getPostMutation(searchStringAll);
				if (postResAll?.success) {
					const { docs = {}, meta = {} } = postResAll?.data || {};
					const { results = [] } = docs;
					if (results?.length > 0) {

						setForYouFeeds(results);
									setForYouFeedsMeta(results.meta);
									setForYouCurrentCursor(results.meta?.nextCursor || 0);
									setForYouLastcursor(results.meta?.nextCursor || 0);

						// setFeeds(results);
						// setFeedsMeta(meta);
						// setLastFeedPage(meta?.page || 1);
					}
				}

				if (modalConfig?.onClose) {
					modalConfig.onClose();
				}
			}
		} catch (error) {
			console.error('Error saving feeds:', error);
			toast.error(translate('ToastMessage.titleFailed', { title: translate('Common.savePost') })); // Notify the user of the failure
		} finally {
			setIsLoading(false);
			logPostCreated('text');
		}
	};

	const handleAddEmoji = (emojiData: any) => {
		const emoji = emojiData.native;
		const messageInput = document.querySelector('textarea[id="content"]') as HTMLTextAreaElement;
		if (messageInput) {
			const { selectionStart, selectionEnd } = messageInput;
			const message = formik.values.content || '';

			// Insert emoji at the cursor position
			const newMessage = message.slice(0, selectionStart) + emoji + message.slice(selectionEnd);

			// Update the message with the new text and reposition the cursor
			formik.setFieldValue('content', newMessage);

			// Set the cursor position after the inserted emoji
			setTimeout(() => {
				messageInput.selectionStart = messageInput.selectionEnd = selectionStart + emoji.length;
			}, 0);
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [openEmojiPicker]);

	const handleClickOutside = (e: any) => {
		if (!emojiRef?.current?.contains(e.target) && !emojiBtnRef?.current?.contains(e.target)) {
			setOpenEmojiPicker(e.unified);
		}
	};

	const renderError = <T extends keyof AddPostInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Form onSubmit={formik.handleSubmit}>
			<Modal.Body className="p-30">
				<Button
					className="close-btn rounded-circle border-0 fw-bold position-absolute"
					onClick={() => hideCommonModal()}
				>
					<i className="ri-close-line"></i>
				</Button>
				<Stack className="create-new-post gap-4">
					<h4 className="fw-bold mb-0 text-center">{`${postId ? translate('Modal.updatePost') : translate('Modal.createNewPost')}`}</h4>

					<div className="d-flex align-items-sm-center justify-content-between mt-sm-3">
						<div className="create-new-post-profile d-flex flex-column flex-sm-row align-items-sm-center gap-2 gap-sm-3">
							<div className="create-new-post-profile-img">
								<Image
									src={
										authUser?.profilePicture
											? `${S3_URL}${authUser.profilePicture}`
											: IMAGE_PATHS.UserDefault
									}
									className="w-100 h-100 rounded-circle object-fit-cover"
									loading="lazy"
								/>
							</div>
							<div>
								<h4 className="fw-bold mb-1">
									{authUser?.userType === USER_TYPES?.COMPANY
										? authUser?.companyName
										: `${authUser?.name} ${authUser?.lastName}`}
								</h4>
								<p className="create-new-post-profile-designation mb-0">{authUser?.designation}</p>
							</div>
						</div>
						<div>
							<Button type="submit" className="filled-btn rounded-pill">
								{`+ ${postId ? translate('Social.updatePost') : translate('Social.createPost')}`}
							</Button>
						</div>
					</div>
					<div className="create-new-post-content">
						<MentionsComponent
							formik={formik}
							fieldName={'content'}
							contentValue={content || ''}
							textAreaId={`content`}
						/>
						{!isMobile && (
							<div className="emoji-picker-box">
								<Button
									className="border-0 p-0 bg-transparent"
									onClick={() => setOpenEmojiPicker(!openEmojiPicker)}
									ref={emojiBtnRef}
								>
									<i className="ri-emotion-happy-fill"></i>
									<span className="emoji-picker-tooltip">{translate('Social.emoji')}</span>
								</Button>

								{openEmojiPicker && (
									<div className="emoji-picker-box" ref={emojiRef}>
										<Picker
											data={data}
											onEmojiSelect={handleAddEmoji}
											showPreview={false}
											className="emoji-picker"
											maxFrequentRows={1}
											locale={activeLanguage}
											emojiButtonSize={30}
											emojiSize={18}
											previewPosition={'none'}
										/>
									</div>
								)}
							</div>
						)}
					</div>
					<p className="m-0 p-0 text-danger">*{translate('Common.keepTheContentRespectful')}</p>
					{postId ? (
						feedDetails?.mediaItems?.length > 0 ? (
							<div className="create-new-post-add-media position-relative mb-3">
								<div className="added-media d-flex gap-3 gap-sm-4 flex-wrap">
									{feedDetails?.mediaItems?.map((media: any, index: number) => {
										const mediaUrl =
											media?.mediaType === 'video'
												? S3_SMALL_THUMB + getFileName(media?.file)
												: S3_URL + media?.file;

										return (
											<div key={`${index}-preview`} className="added-media-box position-relative">
												<Image
													src={mediaUrl}
													className="w-100 h-100 object-fit-cover"
													loading="lazy"
												/>
											</div>
										);
									})}
								</div>
							</div>
						) : (
							<></>
						)
					) : (
						<div className="create-new-post-add-media position-relative mb-3">
							<div className="added-media d-flex gap-3 gap-sm-4 flex-wrap">
								{thumbnails?.map((media, index) => (
									<div
										key={`thumbnail-${index}-${media.substring(0, 20)}`}
										className="added-media-box position-relative"
									>
										<Image src={media} className="w-100 h-100 object-fit-cover" loading="lazy" />
										<Button
											className="remove-media-btn rounded-circle border-0 fw-bold position-absolute"
											onClick={() => handleRemoveMedia(index)}
										>
											<i className="ri-close-line"></i>
										</Button>
									</div>
								))}
								{formik?.values?.mediaItems?.length < 10 && (
									<div className="add-media-btn position-relative">
										<Form.Control
											className="border-0"
											type="file"
											multiple
											onChange={handleMediaChange}
											accept="video/*,image/*"
										/>
										<i className="ri-add-circle-fill position-absolute top-50 start-50 translate-middle"></i>
									</div>
								)}
								{renderError('content')}
								{renderError('mediaItems')}
								{error && <span className="text-danger f-error">{error as ReactNode}</span>}
							</div>
						</div>
					)}
				</Stack>
			</Modal.Body>
		</Form>
	);
};

export default CreateNewPost;
